import React, { useState } from 'react';
import { Portlet } from '../../../_services/PortletService';
import IconDisplay from '../../../components/IconDisplay';
import { Workspace } from './types';
import { useWorkspaces } from './useWorkspaces';
import WorkspaceTabBar from './WorkspaceTabBar';
import WorkspaceContextMenu from './WorkspaceContextMenu';
import CreateWorkspaceDialog from './CreateWorkspaceDialog';
import RenameWorkspaceDialog from './RenameWorkspaceDialog';
import PortletWorkspacePopover from './PortletWorkspacePopover';
import './workspace.css';

interface WorkspaceContainerProps {
  portlets: Portlet[];
  onNavigate: (path: string) => void;
  userId: number;
}

const WorkspaceContainer: React.FC<WorkspaceContainerProps> = ({
  portlets,
  onNavigate,
  userId,
}) => {
  const {
    workspaces,
    activeWorkspace,
    setActiveWorkspace,
    addWorkspace,
    deleteWorkspace,
    renameWorkspace,
    togglePortletInWorkspace,
    removePortletFromWorkspace,
    getPortletWorkspaces,
  } = useWorkspaces(portlets, userId);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Workspace | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    workspace: Workspace;
    anchorEl: HTMLElement;
  } | null>(null);

  // Popover state
  const [popover, setPopover] = useState<{
    portletId: string;
    anchorEl: HTMLElement;
  } | null>(null);

  // Filter portlets for the active workspace
  const visiblePortlets = activeWorkspace.isAllApps
    ? portlets
    : portlets.filter((p) => activeWorkspace.portletIds.includes(p.id));

  // Convert to feature cards
  const featureCards = visiblePortlets.map((portlet) => ({
    id: portlet.id,
    title: portlet.title,
    icon: <IconDisplay icon={portlet.icon} size={40} />,
    description: portlet.description,
    // Handle both built-in portlets (route.path) and link portlets (url)
    path: portlet.route?.path || (portlet as any).url || '#',
    gradient: portlet.gradient || 'var(--gradient-brand)',
    // Track if this is an external link
    isExternalLink: !(portlet.route?.path) && !!(portlet as any).url,
    openInNewTab: (portlet as any).openInNewTab,
    // Track portlet type for group handling
    type: (portlet as any).type || 'standard',
  }));

  const isCustomWorkspace = !activeWorkspace.isAllApps;

  return (
    <div className="features-container">
      <WorkspaceTabBar
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspace.id}
        onSelectWorkspace={setActiveWorkspace}
        onAddWorkspace={() => setShowCreateDialog(true)}
        onOpenContextMenu={(ws, el) => setContextMenu({ workspace: ws, anchorEl: el })}
      />

      <div className="features-grid">
        {featureCards.map((card) => (
          <div
            key={card.id}
            className="feature-card feature-card-ws"
            onClick={() => {
              if (card.type === 'group') {
                onNavigate(`/portlet-group/${card.id}`);
              } else if (card.isExternalLink) {
                if (card.openInNewTab) {
                  window.open(card.path, '_blank');
                } else {
                  window.location.href = card.path;
                }
              } else {
                onNavigate(card.path);
              }
            }}
          >
            <div className="card-content">
              <div className="card-icon">{card.icon}</div>
              <div className="card-text">
                <h3 className="card-title">{card.title}</h3>
                <p className="card-description">{card.description}</p>
              </div>
            </div>

            {/* Pin button - always visible on hover for workspace assignment */}
            <button
              className="card-workspace-pin"
              onClick={(e) => {
                e.stopPropagation();
                setPopover({ portletId: card.id, anchorEl: e.currentTarget as HTMLElement });
              }}
              title="Add to workspace"
            >
              ⊕
            </button>

            {/* Remove button - only in non-All-Apps workspaces */}
            {isCustomWorkspace && (
              <button
                className="card-workspace-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removePortletFromWorkspace(activeWorkspace.id, card.id);
                }}
                title="Remove from this workspace"
                style={{ right: '40px' }}
              >
                ×
              </button>
            )}
          </div>
        ))}

        {/* Empty state */}
        {featureCards.length === 0 && isCustomWorkspace && (
          <div className="workspace-empty-state">
            <p>No apps in this workspace yet</p>
            <p>
              Switch to "All Apps" and click the ⊕ icon on any card to add it here.
            </p>
          </div>
        )}
      </div>

      {/* Context menu for workspace tabs */}
      {contextMenu && (
        <WorkspaceContextMenu
          workspace={contextMenu.workspace}
          anchorEl={contextMenu.anchorEl}
          onRename={() => setRenameTarget(contextMenu.workspace)}
          onDelete={() => deleteWorkspace(contextMenu.workspace.id)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Portlet workspace assignment popover */}
      {popover && (
        <PortletWorkspacePopover
          portletId={popover.portletId}
          workspaces={workspaces}
          portletWorkspaceIds={getPortletWorkspaces(popover.portletId)}
          onToggle={togglePortletInWorkspace}
          anchorEl={popover.anchorEl}
          onClose={() => setPopover(null)}
        />
      )}

      {/* Create workspace dialog */}
      <CreateWorkspaceDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreate={addWorkspace}
      />

      {/* Rename workspace dialog */}
      <RenameWorkspaceDialog
        isOpen={renameTarget !== null}
        workspace={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRename={renameWorkspace}
      />
    </div>
  );
};

export default WorkspaceContainer;
